<script setup>
    import BoolElement from '@/shared/components/BoolElement.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import { longDate, shortDate } from '@/formatters';
    import { requireIsAdministrator as vRequireIsAdministrator } from '@/shared/directives/requirePermission';
    import _ from 'lodash';
    import ViewBaseAccessPolicy from '@/apps/config/AccessPolicy/ViewBaseAccessPolicy.vue';
    import AccessPolicyPicker from '@/apps/config/AccessPolicy/AccessPolicyPicker.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';

    import { ref, onMounted, computed } from 'vue';
    import { useAccessPolicyStore } from '@/stores/accessPolicyStore';
    import useShow from '@/shared/composables/useShow';
    import { BField } from 'buefy';

    // data
    const store = useAccessPolicyStore();
    const { showError, showInfo } = useShow();

    const createNew = ref(false);
    const emailValid = ref(false);
    const emailInput = ref(null);
    const saving = ref(false);
    const mounted = ref(false);
    const loading = ref(false);

    const systemToggleTracker = ref({});
    const expandedSystems = ref({});
    const localRolesStatus = ref({});
    const changedCheckboxes = ref([]);
    const totalScopes = ref({});

    // computed
    const newUserEmailHasValue = computed(() => {
        return store.selectedUser && store.selectedUser.trim().length > 0;
    });

    const groupUserAttributesByEmail = computed(() => {
        return _.chain(store.userAttributes)
            .groupBy(x => [x.email])
            .map(userAttributes => {
                const { email, name } = userAttributes[0];
                return {
                    groupKey: email,
                    systems: new Set(userAttributes.map(x => x.system)),
                    email: email,
                    name: name
                };
            })
            .value();
    });

    const groupedUserAttributes = computed(() => {
        return _.chain(store.userAttributes)
            .groupBy(x => [x.email, x.scope, x.system])
            .map(userAttributes => {
                const { email, name, scope, system, fromCommonLib } = userAttributes[0];
                return {
                    groupKey: `${email}|${scope}|${system}`,
                    email: email,
                    name: name,
                    scope: scope,
                    system: system,
                    fromCommonLib,
                    allFromCommonlib: userAttributes.every(x => x.fromCommonLib),
                    earliestDateTimeExpire: _.min(userAttributes.map(x => x.dateTimeExpire)),
                    isAnyExpired: userAttributes.some(x => x.isExpired),
                    roles: new Map(userAttributes.map(x => [x.role, {
                        isExpired: x.isExpired,
                        expireDate: x.dateTimeExpire
                    }])),
                    userAttributes
                };
            })
            .value();
    });

    const originalRolesStatus = computed(() => {
        const attributes = {};
        store.userAttributes.forEach(userAttribute => {
            const groupKey = `${userAttribute.email}|${userAttribute.scope}|${userAttribute.system}`;
            if (!attributes[groupKey]) {
                attributes[groupKey] = {};
            }
            attributes[groupKey][userAttribute.role] = { isExpired: userAttribute.isExpired };
        });
        return attributes;
    });

    const validNewUserPolicy = computed(() => {
        return store.selectedSystem !== undefined
            && store.selectedRole !== undefined
            && store.selectedScope !== undefined;
    });

    const validPolicyEntries = computed(() => {
        return store.newUserPolicies.length > 0
            && emailValid.value;
    });

    const showEmailValidationError = computed(() => {
        if (emailValid.value === false && store.selectedUser !== undefined) {
            return 'is-danger';
        }

        return '';
    });

    const scopeNames = computed(() => {
        return store.scopes
            .map(scope => scope.identity)
            .sort();
    });

    const systemNames = computed(() => store.policySystems.map(s => s.name));
    const roleNames = computed(() => store.systemRoles.map(r => r.name));

    // loading
    onMounted(async () => {
        loading.value = true;
        // Wait for every load so the role checkboxes always match the user attributes that did load.
        const results = await Promise.allSettled([
            store.loadSystems(),
            store.loadUserAttributes(),
            store.loadScopes()
        ]);
        initializeLocalRolesStatusForAllUsers();
        if (results.some(r => r.status === 'rejected')) {
            showError('Unable to load data');
        }
        loading.value = false;
        mounted.value = true;
    });

    // methods
    function initializeLocalRolesStatusForAllUsers() {
        localRolesStatus.value = _.cloneDeep(originalRolesStatus.value);
    }

    function dataForDetailsTable(email) {
        const expandedSystem = getSystemName(email);
        const data = groupedUserAttributes.value.filter(
            user => user.system === expandedSystem && user.email === email
        );
        totalScopes.value[email] = data.length;
        return data;
    }

    function hasChangesForSelectedUser(row) {
        const expandedSystem = getSystemName(row.email);
        return changedCheckboxes.value.some(
            ({ email, system }) => email === row.email && system === expandedSystem
        );
    }

    function getSystemName(email) {
        return expandedSystems.value[email] || '';
    }

    async function onClickSave() {
        const email = store.selectedUser;
        const rolesToAdd = store.pendingRolesNotYetAssigned();
        if (rolesToAdd.length === 0) {
            showError(`${email} already has these roles`);
            return;
        }

        saving.value = true;
        try {
            const saved = await store.saveUserAttributes();
            if (!saved) {
                showError('Unexpected error while saving role(s)');
                return;
            }

            const alreadyAssigned = store.newUserPolicies.length - rolesToAdd.length;
            showInfo(`Added ${rolesToAdd.length} role(s) for ${email}`
                + (alreadyAssigned > 0 ? ` (${alreadyAssigned} already assigned)` : ''));

            // Stay open with the role list and inputs kept, ready for the next user
            store.selectedUser = undefined;
            emailValid.value = false;
            emailInput.value?.focus();

            loading.value = true;
            try {
                await store.loadUserAttributes();
                initializeLocalRolesStatusForAllUsers();
            } catch {
                showError('Unexpected error while loading user roles');
            } finally {
                loading.value = false;
            }
        } finally {
            saving.value = false;
        }
    }

    function onClickAddRow() {
        if (!store.addUserPolicy()) {
            showError('This role is already in the list');
        }
    }

    function onClickRemoveAttribute(row) {
        const removed = store.removeNewUserAttributes(row);
        if (!removed) {
            showError('Unexpected error while removing role');
        }
    }

    function onOpen() {
        store.clearSelection();
        emailValid.value = false;
        createNew.value = true;
    }

    function onCancel() {
        createNew.value = false;
        store.clearSelection();
        emailValid.value = false;
    }

    function hasRole(row, role) {
        const roleStatus = localRolesStatus.value[row.groupKey]?.[role];
        return roleStatus && !roleStatus.isExpired;
    }

    function hasExpireDate(row, role) {
        return row.roles.get(role)?.expireDate && row.roles.get(role)?.expireDate !== '0001-01-01T00:00:00';
    }

    const getSystemRoles = (email) => {
        const system = getSystemName(email);
        return store.policySystems
            .find(s => s.name === system)
            ?.roles
            ?.map(role => role.name)
            .sort()
            || [];
    };

    function handleOnChangeCheckbox(row, role, isChecked) {
        const { groupKey, email, system, scope, fromCommonLib } = row;
        localRolesStatus.value[groupKey] ??= {};
        localRolesStatus.value[groupKey][role] = {
            isExpired: !isChecked
        };

        const changeIndex = changedCheckboxes.value.findIndex(
            change => change.groupKey === groupKey && change.role === role
        );
        if (changeIndex !== -1) {
            changedCheckboxes.value.splice(changeIndex, 1);
        } else {
            changedCheckboxes.value.push({
                groupKey,
                role,
                isChecked,
                email,
                system,
                scope,
                fromCommonLib
            });
        }
    }

    function toggleSystem(userProps, system) {
        const { row, toggleDetails } = userProps;
        const email = row.email;

        systemToggleTracker.value[email] ||= [];

        systemToggleTracker.value[email].forEach(s => {
            if (s.name !== system && !s.isCollapsed) {
                s.isCollapsed = true;
                toggleDetails(row);
            }
        });

        const systemEntry = systemToggleTracker.value[email].find(s => s.name === system);

        if (systemEntry) {
            systemEntry.isCollapsed = !systemEntry.isCollapsed;
            if (systemEntry.isCollapsed) {
                delete expandedSystems.value[email];
            } else {
                expandedSystems.value[email] = system;
            }
        } else {
            systemToggleTracker.value[email].push({ name: system, isCollapsed: false });
            expandedSystems.value[email] = system;
        }
        toggleDetails(row);
    }

    function isSystemExpanded(email, system) {
        const systemEntry = systemToggleTracker.value[email]?.find(s => s.name === system);
        return systemEntry ? !systemEntry.isCollapsed : false;
    }

    function isUserAssignedRoleInSystem(group, system) {
        return group.systems.has(system);
    }

    function validateEmail() {
        if (!newUserEmailHasValue.value) {
            emailValid.value = false;
            return;
        }

        // Keep an invalid address in the field (marked red) so it can be corrected instead of retyped
        emailValid.value = validateEmailAndModify(store.selectedUser);
        if (!emailValid.value) {
            showError('Invalid email address, must be of format USERNAME@equinor.com');
        }
    }

    function validateEmailAndModify(email) {
        if (!newUserEmailHasValue.value) return false;

        if (!email.includes('@')) {
            email = `${email}@equinor.com`;
        }

        const split = email.split('@', 2);
        const mailToTest = `${split[0].toUpperCase()}@${split[1].toLowerCase()}`;

        const mailFormat = /^[^@\sa-z]{2,64}@equinor.com$/;
        if (!mailToTest.match(mailFormat)) return false;

        store.selectedUser = mailToTest;
        return true;
    }

    function revertChangesInRoleState(row, system) {
        const changesToRevert = changedCheckboxes.value.filter(change => {
            return change.email === row.email && change.system === system;
        });
        changesToRevert.forEach(change => {
            const { groupKey, role } = change;
            if (originalRolesStatus.value[groupKey]) {
                localRolesStatus.value[groupKey][role] = originalRolesStatus.value[groupKey][role];
            }
        });
    }

    function revertChangedCheckBoxes(row, system) {
        changedCheckboxes.value = changedCheckboxes.value.filter(change => {
            return !(change.email === row.email && change.system === system);
        });
    }

    function onCancelDetailsTable(row, system) {
        revertChangesInRoleState(row, system);
        revertChangedCheckBoxes(row, system);
        store.clearSelection();
    }

    function formatDate(date, fromCommonLib) {
        if (date === '0001-01-01T00:00:00')
            return fromCommonLib ? 'No expiration date' : 'Unknown';
        return shortDate(date);
    }

    async function onClickSaveDetailsTable(row) {
        const expandedSystem = getSystemName(row.email);
        store.selectedUser = row.email;
        const relevantChanges = changedCheckboxes.value.filter(change =>
            change.system === expandedSystem && change.email === row.email);

        relevantChanges.forEach(change => {
            const { groupKey, role, isChecked, system, scope, email, fromCommonLib } = change;
            const userAttribute = store.userAttributes.find(user =>
                user.email === email
                && user.system === system
                && user.scope === scope
                && user.role === role);

            const originalRole = originalRolesStatus.value[groupKey]?.[role];

            if (!originalRole) {
                store.newUserPolicies.push({
                    email,
                    scope,
                    sourceSystem: system,
                    facilityRole: role,
                    commonLibrary: true,
                    dateTimeExpire: ''
                });
            } else {
                store.updateUserPolicies.push({
                    id: userAttribute.policyId,
                    email,
                    scope,
                    sourceSystem: system,
                    facilityRole: role,
                    commonLibrary: fromCommonLib,
                    extend: isChecked
                });
            }
        });

        try {
            const saved = await store.saveUserAttributes();
            if (!saved) {
                showError('Unexpected error while saving role(s)');
            } else {
                loading.value = true;
                await store.loadUserAttributes();
            }
        } catch (error) {
            showError('Unexpected error while loading user roles');
        } finally {
            onCancelDetailsTable(row, expandedSystem);
            store.clearSelection();
            loading.value = false;
        }
    }
</script>

<template>
    <view-base-access-policy>
        <template
            #controls>
            <div class="level">
                <div class="level-left mt-2 mb-3">
                    <b-button
                        v-require-is-administrator
                        class="is-info"
                        @click="onOpen">
                        <span class="icon is-small">
                            <i class="mdi mdi-plus" />
                        </span>
                        <span>
                            Create new user role
                        </span>
                    </b-button>
                </div>
            </div>
        </template>
        <template #modal>
            <modal-base
                v-if="createNew"
                @onCancel="onCancel">
                <template #modal-header>
                    Create new user role
                </template>
                <template
                    #modal-body>
                    <p class="title is-5 mb-4">
                        Roles to add
                    </p>
                    <div class="columns is-2">
                        <div class="column">
                            <b-field label="System">
                                <access-policy-picker
                                    :model-value="store.selectedSystem?.name ?? null"
                                    :options="systemNames"
                                    placeholder="Select system"
                                    @update:model-value="store.setSelectedSystem" />
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Role">
                                <access-policy-picker
                                    :model-value="store.selectedRole?.name ?? null"
                                    :options="roleNames"
                                    :disabled="roleNames.length === 0"
                                    placeholder="Select a Role"
                                    @update:model-value="store.setSelectedRole" />
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Scope">
                                <access-policy-picker
                                    :model-value="store.selectedScope ?? null"
                                    :options="scopeNames"
                                    placeholder="Select scope"
                                    @update:model-value="store.setSelectedScope" />
                            </b-field>
                        </div>
                        <div class="column is-narrow">
                            <b-field label="&nbsp">
                                <b-button
                                    class="is-info"
                                    :disabled="!validNewUserPolicy"
                                    @click="onClickAddRow">
                                    <span class="icon is-small">
                                        <i class="mdi mdi-plus" />
                                    </span>
                                </b-button>
                            </b-field>
                        </div>
                    </div>
                    <b-table
                        v-if="store.newUserPolicies.length > 0"
                        :data="store.newUserPolicies">
                        <b-table-column
                            v-slot="props"
                            label="System"
                            field="sourceSystem">
                            {{ props.row.sourceSystem }}
                        </b-table-column>
                        <b-table-column
                            v-slot="props"
                            label="Role"
                            field="facilityRole">
                            {{ props.row.facilityRole }}
                        </b-table-column>
                        <b-table-column
                            v-slot="props"
                            label="Scope"
                            field="scope">
                            {{ props.row.scope }}
                        </b-table-column>
                        <b-table-column
                            v-slot="props"
                            label="&nbsp">
                            <a @click="onClickRemoveAttribute(props.row)">
                                <b-icon
                                    icon="delete"
                                    size="is-small" />
                            </a>
                        </b-table-column>
                    </b-table>
                    <p class="title is-5 mt-6 mb-4">
                        Add the roles to this user
                    </p>
                    <b-field :type="showEmailValidationError">
                        <b-input
                            ref="emailInput"
                            v-model="store.selectedUser"
                            v-require-is-administrator
                            type="email"
                            expanded
                            aria-label="E-mail"
                            placeholder="Enter user email"
                            @focusout="validateEmail"
                            @keyup.enter="validateEmail" />
                    </b-field>
                </template>
                <template #modal-footer>
                    <b-field
                        grouped
                        position="right">
                        <b-button
                            class="mt-2"
                            @click="onCancel">
                            Close
                        </b-button>
                        <b-button
                            class="is-primary mt-2"
                            :loading="saving"
                            :disabled="!validPolicyEntries || saving"
                            @click="onClickSave">
                            Add
                        </b-button>
                    </b-field>
                </template>
            </modal-base>
        </template>
        <template #userattributes>
            <p class="block">
                Click the expansion icon to view the user's access roles.
            </p>
            <spinner :loading="!mounted" />
            <b-table
                v-if="mounted"
                :data="groupUserAttributesByEmail"
                :loading="loading"
                default-sort="email"
                striped
                narrowed
                hoverable
                paginated
                detailed
                :show-detail-icon="false"
                per-page="50"
                detail-key="groupKey">
                <b-table-column
                    v-for="(system, index) in store.policySystems.map(s => s.name)"
                    :key="index"
                    width="3ch"
                    header-class="rotate"
                    sortable>
                    <template #header>
                        {{ system }}
                    </template>
                    <template #default="props">
                        <a
                            v-if="isUserAssignedRoleInSystem(props.row, system)"
                            @click="toggleSystem(props, system)">
                            <div class="is-flex is-align-items-center">
                                <i
                                    :class="`mdi
                                    ${isSystemExpanded(props.row.email, system) ? 'mdi-chevron-down' : 'mdi-chevron-right'}`" />
                                <bool-element
                                    :value="isUserAssignedRoleInSystem(props.row, system)"
                                    size="is-small"
                                    class="mr-2"
                                    custom-size="is-small" />
                            </div>
                        </a>
                    </template>
                </b-table-column>
                <template
                    #detail="{ row }">
                    <b-field
                        grouped>
                        <b-field expanded>
                            <p>Roles assigned to the user from system: {{ getSystemName(row.email) }}</p>
                        </b-field>
                        <b-button
                            class="control"
                            label="Discard changes"
                            icon-left="trash-can"
                            :disabled="!hasChangesForSelectedUser(row)"
                            @click="onCancelDetailsTable(row, getSystemName(row.email))" />
                        <b-button
                            class="control is-primary"
                            label="Save"
                            icon-left="floppy"
                            :disabled="!hasChangesForSelectedUser(row)"
                            @click="onClickSaveDetailsTable(row)" />
                    </b-field>
                    <b-table
                        :data="dataForDetailsTable(row.email)"
                        :class="totalScopes[row.email] > 7 ? 'site-sticky-header' : ''"
                        default-sort="scope">
                        <b-table-column
                            label="Scope"
                            field="scope"
                            sortable
                            searchable>
                            <template #searchable="props">
                                <b-autocomplete
                                    v-model="props.filters.scope"
                                    placeholder="Filter by scope"
                                    :data="scopeNames"
                                    open-on-focus
                                    clearable>
                                    <template #default="{ option }">
                                        {{ option }}
                                    </template>
                                </b-autocomplete>
                            </template>
                            <template #default="props">
                                {{ props.row.scope }}
                            </template>
                        </b-table-column>
                        <b-table-column
                            v-for="(role, index) in getSystemRoles(row.email)"
                            v-slot="props"
                            :key="index"
                            sortable
                            :label="role">
                            <b-checkbox
                                :model-value="hasRole(props.row,role)"
                                @change="handleOnChangeCheckbox(props.row, role, $event.target.checked)">
                                <span
                                    v-if="hasExpireDate(props.row, role)"
                                    :class="props.row.roles.get(role)?.isExpired ? 'has-text-danger' : 'has-text-grey'"
                                    :title="`Expiration Date: ${longDate(props.row.roles.get(role)?.expireDate)}`">
                                    {{ shortDate(props.row.roles.get(role)?.expireDate) }}
                                </span>
                            </b-checkbox>
                        </b-table-column>
                        <b-table-column
                            v-slot="props"
                            :key="`${getSystemName(row.email)}-expirationDateEarliest`"
                            field="earliestDateTimeExpire"
                            label="Expiration date (earliest)"
                            sortable>
                            {{ formatDate(props.row.earliestDateTimeExpire, props.row.allFromCommonlib) }}
                            <span
                                v-if="props.row.isAnyExpired"
                                class="tag is-small is-danger">
                                Expired
                            </span>
                        </b-table-column>
                    </b-table>
                </template>
                <b-table-column
                    label="User"
                    field="email"
                    sortable
                    searchable>
                    <template #searchable="props">
                        <b-input
                            v-model="props.filters.email"
                            placeholder="Filter by email" />
                    </template>
                    <template #default="props">
                        <span @click="props.toggleDetails(props.row)">
                            {{ props.row.email }}
                        </span>
                    </template>
                </b-table-column>
                <b-table-column
                    label="Name"
                    field="name"
                    sortable
                    searchable>
                    <template #searchable="props">
                        <b-input
                            v-model="props.filters.name"
                            placeholder="Filter by name" />
                    </template>
                    <template #default="props">
                        {{ props.row.name }}
                    </template>
                </b-table-column>
            </b-table>
        </template>
    </view-base-access-policy>
</template>

<style scoped>
:deep(.table th) {
    vertical-align: bottom;
}

:deep(th.rotate) span {
    width: 0px;
    text-orientation: mixed;
    writing-mode: vertical-rl;
    rotate: 180deg;
}

:deep(.modal-card) {
    width: 1200px;
}

</style>
